import {
  LayoutDashboard,
  Receipt,
  Wallet,
  FileText,
  BookOpen,
  Landmark,
  Leaf,
  Settings,
  Users,
  ChevronDown,
  ClipboardList,
  ExternalLink,
  ShieldAlert,
} from "lucide-react";
import vlePlusLogo from "@/assets/logo.jpeg";

const SETU_SUVIDHA_URL = "https://setusuvidha.com";
import { NavLink } from "@/components/NavLink";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

// Tool keys matching VLE_TOOLS
const TOOL_KEYS = {
  KIOSK_BANKING: "kiosk_banking",
  PIK_VIMA: "pik_vima",
  DAILY_BOOK: "daily_book",
} as const;

export function AppSidebar() {
  const { t } = useLanguage();
  const { tenant } = useAuth();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";

  // Fetch all VLE tools status for this tenant
  const { data: vleTools } = useQuery({
    queryKey: ["vle_tools_sidebar", tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return {};
      
      const { data, error } = await supabase
        .from("vle_tools")
        .select("tool_key, is_enabled")
        .eq("tenant_id", tenant.id);
      
      if (error) return {};
      
      // Convert to map
      const toolsMap: Record<string, boolean> = {};
      data?.forEach((tool) => {
        toolsMap[tool.tool_key] = tool.is_enabled;
      });
      return toolsMap;
    },
    enabled: !!tenant?.id,
    staleTime: 30000,
  });

  // Check if specific tool is enabled
  const isToolEnabled = (toolKey: string): boolean => {
    // Default: Daily Book is always enabled, others are disabled by default
    if (toolKey === TOOL_KEYS.DAILY_BOOK) {
      return vleTools?.[toolKey] !== false;
    }
    return vleTools?.[toolKey] ?? false;
  };

  // Tool visibility
  const showKioskBanking = isToolEnabled(TOOL_KEYS.KIOSK_BANKING);
  const showPikVima = isToolEnabled(TOOL_KEYS.PIK_VIMA);
  const showDailyBook = isToolEnabled(TOOL_KEYS.DAILY_BOOK);
  

  // Main Navigation (always visible)
  const mainNavItems = [
    { title: t('dashboard'), url: '/dashboard', icon: LayoutDashboard },
    { title: t('sales'), url: '/sales', icon: Receipt },
    { title: t('expenses'), url: '/expenses', icon: Wallet },
    { title: t('reports'), url: '/reports', icon: FileText },
  ];

  // Books items - filtered by tool status
  const booksNavItems = [
    ...(showDailyBook ? [{ title: t('daily_book'), url: '/daily-book', icon: BookOpen }] : []),
    ...(showKioskBanking ? [{ title: t('kiosk_book'), url: '/kiosk-book', icon: Landmark }] : []),
  ];

  // Management items - filtered by tool status
  const managementNavItems = [
    ...(showPikVima ? [{ title: t('pik_vima'), url: '/pik-vima', icon: Leaf }] : []),
    ...(showPikVima ? [{ title: 'Vima Settings', url: '/pik-vima-settings', icon: Settings }] : []),
    { title: 'CRM व्यवस्थापन केंद्र', url: '/crm-center', icon: ShieldAlert },
    { title: t('services'), url: '/services', icon: Receipt },
    { title: t('customers'), url: '/customers', icon: Users },
    { title: t('settings'), url: '/settings', icon: Settings },
  ];

  // Document Slip items
  const docSlipNavItems = [
    { title: 'Customer Slip', url: '/setu-suvidha/doc-slip', icon: ClipboardList },
    { title: 'Manage Slips', url: '/document-slip', icon: ClipboardList },
  ];

  // Billing Sidebar
  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border w-64">
      <SidebarHeader className="border-b border-sidebar-border px-4 py-4">
        <div className="flex items-center gap-3">
          <img src={vlePlusLogo} alt="SETU Suvidha Logo" className="h-10 w-10 rounded-lg object-contain" />
          {!collapsed && (
            <div className="flex flex-col gap-0.5">
              <span className="text-base font-semibold text-sidebar-foreground flex items-center gap-2">
                SETU Suvidha
                <span className="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded-full font-medium">v2.0</span>
              </span>
              <span className="text-sm text-muted-foreground">
                Billing System
              </span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="px-3">
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-sm font-medium text-muted-foreground tracking-wide mb-1">
            {!collapsed && 'Main'}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {mainNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                      activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Books - Only show if there are enabled book tools */}
        {booksNavItems.length > 0 && (
          <SidebarGroup>
            <Collapsible defaultOpen className="group/collapsible">
              <CollapsibleTrigger asChild>
                <SidebarGroupLabel className="flex cursor-pointer items-center justify-between text-sm font-medium text-muted-foreground hover:text-foreground tracking-wide mb-1">
                  {!collapsed && 'Books'}
                  <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                </SidebarGroupLabel>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu className="space-y-1">
                    {booksNavItems.map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild>
                          <NavLink
                            to={item.url}
                            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                            activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                          >
                            <item.icon className="h-5 w-5 shrink-0" />
                            {!collapsed && <span>{item.title}</span>}
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </Collapsible>
          </SidebarGroup>
        )}

        {/* Management */}
        <SidebarGroup>
          <Collapsible defaultOpen className="group/collapsible">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className="flex cursor-pointer items-center justify-between text-sm font-medium text-muted-foreground hover:text-foreground tracking-wide mb-1">
                {!collapsed && 'Management'}
                <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu className="space-y-1">
                  {managementNavItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={item.url}
                          className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                          activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                        >
                          <item.icon className="h-5 w-5 shrink-0" />
                          {!collapsed && <span>{item.title}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </Collapsible>
        </SidebarGroup>

        {/* Document Slip Tools */}
        <SidebarGroup>
          <Collapsible defaultOpen className="group/collapsible">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className="flex cursor-pointer items-center justify-between text-sm font-medium text-muted-foreground hover:text-foreground tracking-wide mb-1">
                {!collapsed && 'Document Slip'}
                <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu className="space-y-1">
                  {docSlipNavItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={item.url}
                          className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                          activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                        >
                          <item.icon className="h-5 w-5 shrink-0" />
                          {!collapsed && <span>{item.title}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </Collapsible>
        </SidebarGroup>

        {/* Back to SETU Suvidha Main Site */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <a
                    href={SETU_SUVIDHA_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                  >
                    <ExternalLink className="h-5 w-5 shrink-0" />
                    {!collapsed && <span>Back to SETU Suvidha</span>}
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-3">
        <div className={cn(
          "flex items-center gap-2 rounded-lg p-2 text-sm text-muted-foreground",
          collapsed && "justify-center"
        )}>
          {!collapsed && (
            <span>© {new Date().getFullYear()} SETU Suvidha</span>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
